/**
 * Canvas Zoom Hook
 * Handles mouse wheel to zoom the canvas viewport
 */

import { RefObject, useEffect } from 'react'

/**
 * Hook to enable zooming on a canvas element
 * @param canvasRef - Reference to the canvas element
 * @param onZoom - Callback function called with scale factor and center point
 */
export function useCanvasZoom(
  canvasRef: RefObject<HTMLCanvasElement>,
  onZoom: (scale: number, centerX: number, centerY: number) => void
) {
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault()

      // Get mouse position relative to canvas
      const rect = canvas.getBoundingClientRect()
      const centerX = e.clientX - rect.left
      const centerY = e.clientY - rect.top

      // Calculate zoom factor
      // Positive deltaY = zoom out, negative deltaY = zoom in
      const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1

      // Call the zoom callback with scale factor and center point
      onZoom(zoomFactor, centerX, centerY)
    }

    // Add wheel event listener with passive: false to allow preventDefault
    canvas.addEventListener('wheel', handleWheel, { passive: false })

    return () => {
      canvas.removeEventListener('wheel', handleWheel)
    }
  }, [canvasRef, onZoom])
}
